import { useState, useEffect } from 'react'
import { createTag, subscribeToTags, updateTag, deleteTag, getTagUsageCount } from '../firebase/tags'
import './TagManager.css'

const TagManager = ({ user }) => {
  const [tags, setTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingTag, setEditingTag] = useState(null)
  const [newTagName, setNewTagName] = useState('')
  const [tagUsageCounts, setTagUsageCounts] = useState({})
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!user) return

    // Subscribe to real-time tags updates
    const unsubscribe = subscribeToTags(user.uid, async (tagsData) => {
      setTags(tagsData)
      setLoading(false)

      // Get usage counts for all tags
      const counts = {}
      for (const tag of tagsData) {
        counts[tag.id] = await getTagUsageCount(tag.name, user.uid)
      }
      setTagUsageCounts(counts)
    })

    return () => unsubscribe()
  }, [user])

  const handleAddTag = async (e) => {
    e.preventDefault()
    setError(null)

    if (!newTagName.trim()) {
      setError('Tag name cannot be empty')
      return
    }

    try {
      await createTag(newTagName, user.uid)
      setNewTagName('')
      setShowAddForm(false)
    } catch (error) {
      console.error('Error creating tag:', error)
      setError(error.message || 'Failed to create tag. Tag may already exist.')
    }
  }

  const handleStartEdit = (tag) => {
    setEditingTag(tag.id)
    setNewTagName(tag.name)
    setError(null)
  }

  const handleCancelEdit = () => {
    setEditingTag(null)
    setNewTagName('')
    setError(null)
  }

  const handleUpdateTag = async (tagId) => {
    setError(null)

    if (!newTagName.trim()) {
      setError('Tag name cannot be empty')
      return
    }

    try {
      await updateTag(tagId, newTagName, user.uid)
      setEditingTag(null)
      setNewTagName('')
      
      // Refresh usage counts
      const updatedCount = await getTagUsageCount(newTagName.trim(), user.uid)
      setTagUsageCounts(prev => ({ ...prev, [tagId]: updatedCount }))
    } catch (error) {
      console.error('Error updating tag:', error)
      setError(error.message || 'Failed to update tag. Tag name may already exist.')
    }
  }

  const handleDeleteTag = async (tag) => {
    const usageCount = tagUsageCounts[tag.id] || 0
    
    const message = usageCount > 0
      ? `Delete "${tag.name}"? This tag is used by ${usageCount} contact(s). Do you want to remove it from all contacts?`
      : `Delete "${tag.name}"?`

    if (!window.confirm(message)) {
      return
    }

    const removeFromContacts = usageCount > 0 && window.confirm(
      `Remove "${tag.name}" from all ${usageCount} contact(s)?`
    )

    try {
      await deleteTag(tag.id, user.uid, removeFromContacts)
    } catch (error) {
      console.error('Error deleting tag:', error)
      alert(error.message || 'Failed to delete tag. Please try again.')
    }
  }

  if (loading) {
    return <div className="loading">Loading tags...</div>
  }

  return (
    <div className="tag-manager-page">
      <div className="page-header">
        <div>
          <h2>Tag Manager</h2>
          <p className="subtitle">Manage your contact tags</p>
        </div>
        <button 
          className="btn-primary" 
          onClick={() => {
            setShowAddForm(true)
            setEditingTag(null)
            setNewTagName('')
            setError(null)
          }}
        >
          + New Tag
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {showAddForm && (
        <div className="tag-form-card">
          <h3>Create New Tag</h3>
          <form onSubmit={handleAddTag}>
            <div className="form-group">
              <label htmlFor="newTagName">Tag Name</label>
              <input
                type="text"
                id="newTagName"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Enter tag name"
                autoFocus
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => {
                setShowAddForm(false)
                setNewTagName('')
                setError(null)
              }}>
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                Create Tag
              </button>
            </div>
          </form>
        </div>
      )}

      {tags.length === 0 && !showAddForm ? (
        <div className="empty-state">
          <p>No tags yet. Create your first tag to get started!</p>
        </div>
      ) : (
        <div className="tags-list">
          {tags.map((tag) => (
            <div key={tag.id} className="tag-item">
              {editingTag === tag.id ? (
                <div className="tag-edit-form">
                  <input
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    className="tag-edit-input"
                    autoFocus
                  />
                  <div className="tag-edit-actions">
                    <button
                      className="btn-icon btn-save"
                      onClick={() => handleUpdateTag(tag.id)}
                      title="Save"
                    >
                      ✓
                    </button>
                    <button
                      className="btn-icon btn-cancel"
                      onClick={handleCancelEdit}
                      title="Cancel"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="tag-info">
                    <span className="tag-name">{tag.name}</span>
                    <span className="tag-usage">
                      {tagUsageCounts[tag.id] || 0} contact{(tagUsageCounts[tag.id] || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="tag-actions">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleStartEdit(tag)}
                      title="Edit tag"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDeleteTag(tag)}
                      title="Delete tag"
                    >
                      🗑️
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default TagManager

